'use client';

import { Toaster } from '@/components/ui/sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ShopifyAnalytics } from '@veranda/shared/cart/components';
import { Check, LoaderIcon, X } from 'lucide-react';
import { type ReactNode, Suspense } from 'react';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  // This is very important so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

interface ProvidersProps {
  children: ReactNode;
}

export default function Providers({ children }: ProvidersProps) {
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense>
        <ShopifyAnalytics hasConsent={true} />
      </Suspense>
      {/* <ReactQueryDevtools buttonPosition="top-right" /> */}

      {children}
      <Toaster
        position="top-right"
        duration={2500}
        expand={true}
        icons={{
          success: <Check className="mr-2 size-4" />,
          error: <X className="mr-2 size-4" />,
          loading: <LoaderIcon className="mr-2 size-4 animate-spin" />,
        }}
        toastOptions={{
          unstyled: true,
          classNames: {
            toast:
              'bg-brand-burgundy text-white font-clash py-3 px-4 w-80 right-0 flex items-center gap-1 text-sm',
            loader: '!static !translate-x-0 !translate-y-0',
          },
        }}
      />
    </QueryClientProvider>
  );
}
